import { Geometry } from "./Geometry";
import { Letter, SiteLetter } from "./Letter";
import { BatchStatus } from "./LetterBatch";
import { Location } from "./Location";
import { Title } from "./Title";

export type SiteMapData = {
  _id: string;
  title: string;
  notes?: string;
  geometry: Geometry;
  location: Location;
  _stage: {
    color: string;
  };
};

export type SiteMapResponseData = {
  siteMap: SiteMapData;
};

export type SiteOverviewResponseData = {
  siteOverview: {
    _id: string;
    title: string;
    notes: string;
    geometry: Geometry;
    location: Location;
    _stage: {
      color: string;
    };
  };
};

export type OwnershipSummaryResponseData = {
  ownershipSummary: {
    totalNumberOfTitlesSummary: string;
    location: Location;
  };
};

export type LetterBatchesQueryVariables = {
  letterBatchesQueryInput: {
    name?: string;
    from_date?: string;
    to_date?: string;
  };
};

export type LetterBatchesQueryResponseData = {
  letterBatches: {
    id: string;
    name: string;
    team_id: string;
    status: BatchStatus;
    created_at: string;
    updated_at: string;
    letters: Pick<
      Letter,
      | "id"
      | "status"
      | "signed_pdf_url"
      | "recipient_address_line_1"
      | "recipient_city"
      | "recipient_name"
    >[];
    total_price: number;
  }[];
};

export type SelectRecipientsTableQueryVariables = {
  siteIds?: string[];
  stageIds?: string[];
  limit?: number;
  siteLabels?: string[];
  searchString?: string;
  localAuthorities?: string[];
  assignees?: string[];
};

export type SelectRecipientsTableQueryResponseData = {
  selectRecipientsTable: {
    edges: {
      node: {
        address: string;
        title: string;
        titles: Title[];
        _id: string;
        geometry: Geometry;
      };
    }[];
  };
};

export type LettersAccessQueryVariables = {
  siteIds?: string[];
  stageIds?: string[];
  siteLabels?: string[];
  searchString?: string;
  localAuthorities?: string[];
  assignees?: string[];
};

export type LettersAccessQueryResponseData = {
  lettersAccess: {
    edges: {
      node: {
        _id: string;
        lettersAccess: {
          isGranted: boolean;
        };
      };
    }[];
  };
};

export type SiteGeometryResponseData = {
  siteGeometry: {
    geometry: Geometry;
  };
};

export type SentLettersTableQueryResponseData = {
  sentLettersTable: [SiteLetter];
};

export type LetterTabQueryResponseData = {
  letterTab: {
    lettersAccess: {
      isGranted: boolean;
      reason: string | null;
    };
  };
};

export type CreateLettersResponseData = {
  createSiteLetters: [
    {
      letter: {
        signed_pdf_url: string;
      };
    }
  ];
};

export type SendLettersResponseData = {
  sendSiteLetters: [{ id: string; letter_id: string }];
};

export type SiteLetterImageDownloadUrlVariables = {
  s3ObjectKey: string;
};

export type SiteLetterImageDownloadUrlResponse = {
  siteLetterImage: {
    signedDownloadUrl: string;
  };
};

export type SiteLetterTemplate = {
  signature_file_id?: string;
  company_logo_file_id?: string;
  greeting?: string;
  body_text_page_1?: string;
  body_text_page_2?: string;
  sign_off?: string;
  signature_page?: number;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  satellite_image_enabled?: boolean;
  has_footer?: boolean;
  vat_registration_number?: string;
  company_registration_number?: string;
  sender_address_line_1?: string;
  sender_city?: string;
  sender_name?: string;
  sender_post_code?: string;
  sender_province?: string;
};

export type SiteLetterTemplatesResponseData = {
  templates: SiteLetterTemplate[];
};

export type PlaceAtkinsOrderResponseData = {
  data: {
    placeAtkinsOrder: {
      id: string;
      atkinsOrderId: number;
    };
  };
};

export type CreateSiteLetterTemplateResponseData = {
  data: {
    createSiteLetterTemplate: SiteLetterTemplate;
  };
};

export type SiteLetterTemplateInput = SiteLetterTemplate;

export type UpdateSiteLetterTemplateVariables = {
  siteLetterTemplateInput: SiteLetterTemplateInput;
};

export type UpdateSiteLetterTemplateResponseData = {
  data: {
    updateSiteLetterTemplate: SiteLetterTemplate;
  };
};

export type Account = {
  balance: number;
};

export type AccountResponseData = {
  account: {
    balance: number;
  };
};

export enum AtkinsOrderInputErrorMessage {
  AREA_OUT_OF_BOUNDS = "AREA_OUT_OF_BOUNDS",
  ORDER_ALREADY_EXISTS = "ORDER_ALREADY_EXISTS",
  ORDER_MAX_LIMIT_REACHED = "ORDER_MAX_LIMIT_REACHED",
}

export type ValidateAtkinsOrderInputResponse = {
  validateAtkinsOrderInput: {
    canAtkinsOrderBePlaced: boolean;
    errors: AtkinsOrderInputErrorMessage[];
  };
};

export enum CurrencyCode {
  GBP = "GBP",
}

export type SiteLetterPaymentSummary = {
  quantity: number;
  siteLetterUnitPricePostTax: number;
  taxRate: number;
  currency: CurrencyCode;
  total: number;
  subtotal: number;
  taxTotal: number;
};

export type SiteLetterPaymentSummaryInput = {
  quantity: number;
};

export type SiteLetterPaymentSummaryResponseData = {
  siteLetterPaymentSummary: SiteLetterPaymentSummary;
};

export type UpdateLetterBatchInput = {
  letterBatchInput: {
    id: string;
    name: string;
  };
};

export type UpdateLetterBatchesTableResponseData = {
  data: {
    updateLetterBatch: UpdateLetterBatchInput;
  };
};
